import {
  Card,
  Skeleton,
  Flex,
  VStack,
  Th,
  Td,
  Table,
  Tr,
  Tbody,
  Thead,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useEffect, useMemo } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { InvoiceItemInputGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { AccrualsByTAccountMap } from '../utils';
import { CreateInvoiceLineItemsTable } from './CreateInvoiceLineItemsTable';
import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useCreateInvoiceContext } from 'packages/invoices/hooks/useCreateInvoice';
import { useGetInvoiceFacilityFeeBalancesQuery } from 'packages/invoices/requests/invoices.gql.generated';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';

const CreateFacilityFeeInvoiceLineItems = ({
  invoiceComponentIndex,
  accrualsByTAccountMap,
}: {
  invoiceComponentIndex: number;
  accrualsByTAccountMap: AccrualsByTAccountMap;
}) => {
  const {
    invoice,
    invoiceComponentsWithInvoiceItems,
    updateInvoice,
    updateInvoiceComponent,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
  } = useCreateInvoiceContext();

  useEffect(() => {
    const invoiceItems =
      invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems;
    const accrualsExist = Object.keys(accrualsByTAccountMap).length > 0;

    // Leave the existing invoice items alone if they are present.
    // In places where recalculation of invoice items are needed with accrual refetch,
    // first resetInvoiceComponents()
    if (invoiceItems.length > 0 || !accrualsExist) {
      return;
    }

    const newInvoiceItems: InvoiceItemInputGQLType[] = _.compact(
      _.map(accrualsByTAccountMap, (accountAccrual) => {
        const {
          id,
          tAccount,
          organizationId,
          creditFacilityId,
        } = accountAccrual.tAccount;

        return {
          accountingAccountId: id,
          amount: commonUtils.jsRound({
            value: accountAccrual.totalAccruedAmount,
            precision: 2,
          }),
          status: 'created',
          accountingAccount: {
            id,
            tAccount,
            organizationId,
            creditFacilityId,
          },
          finleyAccruedAmount: accountAccrual.totalAccruedAmount,
        };
      }),
    );

    updateInvoiceItemsAndTotalsForInvoiceComponent({
      invoiceComponentIndex,
      newInvoiceItemsForInvoiceComponent: newInvoiceItems,
    });
  }, [
    invoice.creditFacilityId,
    updateInvoice,
    invoiceComponentIndex,
    invoiceComponentsWithInvoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
    invoice.paymentDestinationOrganizationId,
    accrualsByTAccountMap,
  ]);

  const invoiceItemsExist =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems
      .length > 0;

  return (
    <Flex direction="column" mb="24px">
      <VStack mb="40px" zIndex="dropdown" w="100%" alignItems="flex-start">
        <Typography variant="base" fontWeight="bold">
          Accrual Dates
        </Typography>
        <FullDateRangePicker
          width="100%"
          onSelectDateRange={(newDateRange) => {
            const [newStartDate, newEndDate] = newDateRange;
            if (newStartDate && newEndDate) {
              updateInvoiceComponent({
                invoiceComponentIndex,
                field: 'accrualStartDate',
                value: commonDate.dateToFullDateString(newStartDate),
              });
              updateInvoiceComponent({
                invoiceComponentIndex,
                field: 'accrualEndDate',
                value: commonDate.dateToFullDateString(newEndDate),
              });

              // whenever the accrual dates change, we want to reset all the invoice items
              const newInvoiceItems: InvoiceItemInputGQLType[] = [];

              updateInvoiceItemsAndTotalsForInvoiceComponent({
                invoiceComponentIndex,
                newInvoiceItemsForInvoiceComponent: newInvoiceItems,
              });
            }
          }}
          currentDateRange={{
            startDate: commonDate.fullDateStrToDate(
              invoiceComponentsWithInvoiceItems[invoiceComponentIndex]
                .invoiceComponent.accrualStartDate,
            ),
            endDate: commonDate.fullDateStrToDate(
              invoiceComponentsWithInvoiceItems[invoiceComponentIndex]
                .invoiceComponent.accrualEndDate,
            ),
          }}
          showPreSelectedOptions={false}
        />
      </VStack>

      {invoiceItemsExist ? (
        <CreateInvoiceLineItemsTable
          accrualsByTAccountMap={accrualsByTAccountMap}
          areAllComponentsFundedOnLoan
          totalAccruedAndProjectedOutstandingBalanceAmount={0}
          invoiceComponentIndex={invoiceComponentIndex}
          disableInputs
        />
      ) : (
        <Table borderWidth="1px" borderColor={colors.gray[100]}>
          <Thead>
            <Tr>
              <Th
                w="20%"
                backgroundColor={colors.background[100]}
                fontWeight={600}
                fontSize="14px"
              >
                Item
              </Th>
              <Th
                w="20%"
                backgroundColor={colors.background[100]}
                fontWeight={600}
                fontSize="14px"
              >
                Outstanding Amount
              </Th>
              <Th
                w="56%"
                backgroundColor={colors.background[100]}
                fontWeight={600}
                fontSize="14px"
              >
                Amount To Pay
              </Th>
              <Th w="4%" />
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td colSpan={3}>
                No facility fees found for credit facility during this period.
              </Td>
            </Tr>
          </Tbody>
        </Table>
      )}
    </Flex>
  );
};

export const CreateInvoiceFacilityFeeInvoiceComponent = ({
  invoiceComponentIndex,
}: {
  invoiceComponentIndex: number;
}) => {
  const {
    invoice: { creditFacilityId },
    invoiceComponentsWithInvoiceItems,
  } = useCreateInvoiceContext();

  const invoiceComponent =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceComponent;

  const {
    data: invoiceFacilityFeeBalancesData,
    loading: isInvoiceFacilityFeeBalancesLoading,
    error: invoiceFacilityFeeBalancesError,
  } = useGetInvoiceFacilityFeeBalancesQuery({
    variables: {
      creditFacilityId,
      startDate: invoiceComponent.accrualStartDate,
      endDate: invoiceComponent.accrualEndDate,
    },
  });

  const facilityFeeAccrualsByTAccountMap = useMemo(() => {
    const facilityFeeAccrualsByTAccount: AccrualsByTAccountMap = {};

    if (invoiceFacilityFeeBalancesData) {
      invoiceFacilityFeeBalancesData.getInvoiceFacilityFeeBalances.forEach(
        (facilityFeeBalance) => {
          const accountId = facilityFeeBalance.account.id;

          // there should be only one record returned per tAccount id from useGetInvoiceFacilityFeeBalancesQuery, as it is an aggregate query
          facilityFeeAccrualsByTAccount[accountId] = {
            tAccount: facilityFeeBalance.account,
            actualAccruedAmount: facilityFeeBalance.totalAccruedAmount,
            projectedAccruedAmount: 0, // we are not supporting projected accruals for facility fees
            totalAccruedAmount: facilityFeeBalance.totalAccruedAmount,
          };
        },
      );
    }

    return facilityFeeAccrualsByTAccount;
  }, [invoiceFacilityFeeBalancesData]);

  if (invoiceFacilityFeeBalancesError) {
    throw invoiceFacilityFeeBalancesError;
  }

  if (isInvoiceFacilityFeeBalancesLoading || !invoiceFacilityFeeBalancesData) {
    return <Skeleton height="100%" />;
  }

  return (
    <Card variant="outline" p="40px">
      <CreateFacilityFeeInvoiceLineItems
        invoiceComponentIndex={invoiceComponentIndex}
        accrualsByTAccountMap={facilityFeeAccrualsByTAccountMap}
      />
    </Card>
  );
};
